import { TextDirection } from '../../../../enums';
import { WordToken } from './WordToken';
import { TextMetricList } from './TextMetricList';
import type { WordElementMetrics } from './WordElementMetrics';

// in line = "hello, my name is"
// end of line = "this is the end."
const IN_LINE_PUNCTUATIONS = '.!?:;。！？：；';
const END_OF_LINE_PUNCTUATIONS = ':;,：；、，';

/**
 * Closes a sentence, as opposed to {@link IN_LINE_PUNCTUATIONS}, which is the wider set of places
 * a line may break: a colon or semicolon is a fine place to break but does not end a sentence.
 */
export const SENTENCE_END_PUNCTUATIONS = '.!?。！？…';

/**
 * Represents the measurement of a word.
 */
export class WordMetrics extends TextMetricList<WordElementMetrics> {
    get elements(): WordElementMetrics[] {
        return this.children;
    }

    slice(start: number, end: number): WordToken {
        // clamp values to bounds of the internal text length
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(end, this.text.length);
        let gIndex = 0;

        const slicedWordToken = new WordToken();

        this.elements.forEach((element) => {
            const wordElStart = clampedStart - gIndex;
            const wordElEnd = clampedEnd - gIndex;
            const numChars = element.text.length;

            if (wordElStart < numChars && wordElEnd > 0) {
                const slicedWordElementToken = element.slice(wordElStart, wordElEnd);
                slicedWordToken.add(slicedWordElementToken);
            }
            gIndex += numChars;
        });

        return slicedWordToken;
    }

    /**
     * Reference index to the source text that this token is a part of.
     */
    get sourceWordReferenceIndex() {
        if (this.elements.length === 0) {
            throw new Error('Cannot get sourceWordReferenceIndex, no word elements exist');
        }
        return this.elements[0].sourceWordReferenceIndex;
    }

    override _getSourceWordReferenceIndices(): never {
        throw new Error('WordMetrics does not support getSourceWordReferenceIndices');
    }

    setTextDirection(lineOffsetX: number, advanceXOffset: number, textDirection: TextDirection) {
        let advanceX = advanceXOffset;
        this.elements.forEach((wordElement) => {
            if (textDirection === TextDirection.LTR) {
                wordElement.x = lineOffsetX + wordElement.x;
                wordElement.advanceX = advanceX;
                advanceX += wordElement.advanceWidth;
            } else {
                wordElement.x = lineOffsetX - wordElement.advanceWidth - wordElement.x;
                wordElement.advanceX = lineOffsetX - advanceX - wordElement.advanceWidth;
                advanceX += wordElement.advanceWidth;
            }
        });
        this.clearCachedValues();
    }

    resetWordElementMetrics() {
        this.clearCachedValues();
        this.elements.forEach((element) => element.hide());
    }

    endsWithInLinePunctuation(): boolean {
        return !!this.text.match(`[${IN_LINE_PUNCTUATIONS}]$`);
    }

    endsWithEndOfLinePunctuation(): boolean {
        return !!this.text.match(`[${END_OF_LINE_PUNCTUATIONS}]$`);
    }

    removeLastCharacter() {
        if (this.elements.length === 0) {
            throw new Error('Cannot remove last character from an empty word');
        }
        this.elements[this.elements.length - 1].removeLastCharacter();
        this.clearCachedValues();
    }
}
